/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Promotional Campaign Rail
   Project: La Troupe
   File: assets/ll-promo.js
   Version: 1.0
   Status: LLCL-QA

   ==========================================================
   RESPONSIBILITY
   ==========================================================

   JavaScript owns ONLY:

   - collapsed / expanded state
   - viewport motion triggers
   - manual open / close control
   - accessibility state
   - Shopify Theme Editor lifecycle

   Liquid + CSS own:

   - typography
   - spacing
   - rail height
   - content
   - promo code
   - collection URL
   - CTA
   - colors
   - responsive layout

   ==========================================================
   MOTION MODEL
   ==========================================================

   SCROLL DOWN:

   promotion below viewport
            ↓
        COLLAPSED

   rail reaches OPEN LINE
   default ≈ 64%
            ↓
         EXPAND

   rail travels through viewport
            ↓
        EXPANDED

   rail reaches CLOSE LINE
   default ≈ 18%
            ↓
        COLLAPSE

   user continues normally
   to next Home section


   SCROLL UP:

   Exact sequence reverses naturally.


   ==========================================================
   STRICT
   ==========================================================

   NO:
   - fixed
   - sticky
   - popup
   - overlay
   - scroll hijacking
   - scroll locking
   - DOM duplication
   - layout rewriting

   ========================================================== */

(() => {
  'use strict';


  /* ========================================================
     00 / CONFIG
     ======================================================== */

  const VERSION = '1.1-casa';

  const MANAGER_KEY =
    'LLCasaMotionManager';

  const ROOT_SELECTOR =
    '[data-ll-casa]';

  /*
   * Distance visitor must scroll after manually opening
   * or closing before automatic storytelling takes over.
   */

  const MANUAL_RELEASE_DISTANCE =
    110;



  /* ========================================================
     01 / OLD INSTANCE PROTECTION

     Shopify Theme Editor may execute this asset more than
     once when sections are re-rendered.

     One manager only.
     ======================================================== */

  const previousManager =
    window[MANAGER_KEY];


  if (
    previousManager &&
    previousManager.version === VERSION
  ) {

    previousManager.scan(document);

    return;

  }


  if (
    previousManager &&
    typeof previousManager.destroy === 'function'
  ) {

    previousManager.destroy();

  }



  /* ========================================================
     02 / HELPERS
     ======================================================== */

  const toNumber = (
    value,
    fallback
  ) => {

    const parsed =
      Number(value);


    return Number.isFinite(parsed)
      ? parsed
      : fallback;

  };


  const clamp = (
    value,
    minimum,
    maximum
  ) => {

    return Math.min(
      Math.max(
        value,
        minimum
      ),
      maximum
    );

  };


  const getViewportHeight =
    () => {

      return (
        window.innerHeight ||
        document.documentElement.clientHeight ||
        1
      );

    };


  const reducedMotion =
    () => {

      return (
        window.matchMedia &&
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      );

    };



  /* ========================================================
     03 / PROMO CONTROLLER
     ======================================================== */

  class LLPromoController {


    constructor(root) {


      this.root =
        root;


      this.bar =
        root.querySelector(
          '[data-ll-promo-bar]'
        );


      this.drawer =
        root.querySelector(
          '[data-ll-promo-drawer]'
        );


      this.toggle =
        root.querySelector(
          '[data-ll-promo-toggle]'
        );


      /*
       * Internal state.
       */

      this.expanded =
        false;


      this.previewLock =
        false;


      this.manualOverride =
        false;


      this.manualStartY =
        window.scrollY;


      this.destroyed =
        false;


      this.rafPending =
        false;


      this.resizeTimer =
        null;


      this.openLine =
        64;


      this.closeLine =
        18;


      /*
       * Bind once so listeners can also be removed cleanly.
       */

      this.onScroll =
        this.onScroll.bind(this);


      this.onResize =
        this.onResize.bind(this);


      this.onToggle =
        this.onToggle.bind(this);


      /*
       * Do not initialize incomplete markup.
       */

      if (
        !this.bar ||
        !this.drawer
      ) {

        return;

      }


      this.readSettings();

      this.init();

    }



    /* ======================================================
       04 / READ LIQUID SETTINGS
       ====================================================== */

    readSettings() {


      this.openLine =
        clamp(
          toNumber(
            this.root.dataset.openLine,
            64
          ),
          40,
          90
        );


      this.closeLine =
        clamp(
          toNumber(
            this.root.dataset.closeLine,
            18
          ),
          0,
          45
        );


      /*
       * Prevent contradictory trigger zones.
       */

      if (
        this.closeLine >=
        this.openLine - 12
      ) {

        this.closeLine =
          Math.max(
            6,
            this.openLine - 38
          );

      }


      /*
       * Liquid only adds this class when:
       *
       * request.design_mode = true
       * AND
       * Vista de edición = Módulo expandido
       *
       * Therefore this is safe as an editor-only lock.
       */

      this.previewLock =
        this.root.classList.contains(
          'is-preview-expanded'
        );

    }



    /* ======================================================
       05 / INIT
       ====================================================== */

    init() {


      this.root.classList.add(
        'is-js-ready'
      );


      /*
       * Enable control that Liquid deliberately ships
       * disabled before JS is available.
       */

      this.enableToggle();


      window.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        this.onResize,
        {
          passive: true
        }
      );


      /*
       * If Theme Editor explicitly requests expanded preview,
       * preserve it.
       *
       * Otherwise JS owns state from this point forward.
       */

      if (
        this.previewLock
      ) {

        this.setExpanded(
          true,
          'editor-preview'
        );

      } else {


        this.root.classList.remove(
          'is-preview-expanded'
        );


        this.setExpanded(
          false,
          'initial'
        );


        /*
         * Wait one paint frame before measuring the actual
         * rail position.
         */

        window.requestAnimationFrame(
          () => {


            if (
              this.destroyed
            ) {

              return;

            }


            this.syncFromGeometry(
              'initial-geometry'
            );


          }
        );

      }

    }



    /* ======================================================
       06 / ENABLE TOGGLE
       ====================================================== */

    enableToggle() {


      if (
        !this.toggle
      ) {

        return;

      }


      /*
       * CSS foundation has pointer-events:none.
       * JS now safely activates the button.
       */

      this.toggle.style.pointerEvents =
        'auto';


      this.toggle.style.cursor =
        'pointer';


      this.toggle.removeAttribute(
        'aria-disabled'
      );


      this.toggle.removeAttribute(
        'tabindex'
      );


      this.toggle.addEventListener(
        'click',
        this.onToggle
      );

    }



    /* ======================================================
       07 / VIEWPORT MOTION ENGINE

       We measure the CENTER of the compact rail.

       Why center instead of top?

       It creates a cleaner optical trigger and remains
       stable if merchant changes rail height in Shopify.

       Opening the drawer does NOT alter the position of the
       rail itself because content expands below it in normal
       document flow.
       ====================================================== */

    syncFromGeometry(source = 'geometry') {
      if (this.destroyed) {
        return;
      }

      if (this.previewLock) {
        this.setExpanded(true, 'editor-preview-lock');
        return;
      }

      if (this.manualOverride) {
        return;
      }

      const rail = this.bar.getBoundingClientRect();
      const viewport = getViewportHeight();
      const whole = this.root.getBoundingClientRect();

      const probe =
        rail.top +
        rail.height * 0.5;

      const openingThreshold =
        viewport *
        (this.openLine / 100);

      const exitThreshold =
        viewport *
        (this.closeLine / 100);

      /*
       * Expanded state is preserved while the visitor is still
       * traversing real campaign content. Collapse only after the
       * complete component has crossed the configured close line,
       * or when reversing upward and the rail has almost left below.
       */
      if (this.expanded) {
        const leavingAbove =
          whole.bottom <= exitThreshold;

        const leavingBelow =
          rail.top >= viewport * 0.94;

        if (leavingAbove || leavingBelow) {
          this.setExpanded(false, source);
        }

        return;
      }

      const railVisible =
        rail.bottom > 0 &&
        rail.top < viewport;

      const readableWindow =
        whole.bottom > exitThreshold;

      if (
        probe <= openingThreshold &&
        railVisible &&
        readableWindow
      ) {
        this.setExpanded(true, source);
      }
    }

    /* Independent Casa scroll thresholds preserve the original click,
       resize, accessibility and Shopify lifecycle controllers. */

    onScroll() {


      if (
        this.destroyed
      ) {

        return;

      }


      /*
       * Release manual override only after meaningful
       * navigation resumes.
       */

      if (
        this.manualOverride
      ) {


        const distance =
          Math.abs(
            window.scrollY -
            this.manualStartY
          );


        if (
          distance >=
          MANUAL_RELEASE_DISTANCE
        ) {

          this.manualOverride =
            false;

        }

      }


      if (
        this.rafPending
      ) {

        return;

      }


      this.rafPending =
        true;


      window.requestAnimationFrame(
        () => {


          this.rafPending =
            false;


          this.syncFromGeometry(
            'scroll'
          );


        }
      );

    }



    /* ======================================================
       09 / RESIZE
       ====================================================== */

    onResize() {


      window.clearTimeout(
        this.resizeTimer
      );


      this.resizeTimer =
        window.setTimeout(
          () => {


            if (
              this.destroyed
            ) {

              return;

            }


            this.manualOverride =
              false;


            this.readSettings();


            this.syncFromGeometry(
              'resize'
            );


          },
          120
        );

    }



    /* ======================================================
       10 / SET STATE
       ====================================================== */

    setExpanded(
      open,
      source = 'unknown'
    ) {


      const next =
        Boolean(open);


      /*
       * Already correct?
       * Still synchronize accessibility.
       */

      if (
        next ===
        this.expanded
      ) {

        this.syncAccessibility(
          next
        );


        return;

      }


      /*
       * Prevent keyboard focus from remaining inside hidden
       * content.
       */

      if (
        !next &&
        document.activeElement &&
        this.drawer.contains(
          document.activeElement
        )
      ) {

        document.activeElement.blur();

      }


      this.expanded =
        next;


      this.root.classList.toggle(
        'is-expanded',
        next
      );


      this.root.classList.toggle(
        'is-collapsed',
        !next
      );


      /*
       * Once normal JS motion owns the component, remove
       * stale preview state.
       */

      if (
        !this.previewLock
      ) {

        this.root.classList.remove(
          'is-preview-expanded'
        );

      }


      this.root.dataset.motionState =
        next
          ? 'expanded'
          : 'collapsed';


      this.syncAccessibility(
        next
      );


      this.emitStateChange(
        source
      );

    }



    /* ======================================================
       11 / ACCESSIBILITY
       ====================================================== */

    syncAccessibility(
      open
    ) {


      this.drawer.setAttribute(
        'aria-hidden',
        open
          ? 'false'
          : 'true'
      );


      /*
       * Prevent hidden CTA from keyboard navigation.
       */

      this.drawer.inert =
        !open;


      if (
        this.toggle
      ) {

        this.toggle.setAttribute(
          'aria-expanded',
          open
            ? 'true'
            : 'false'
        );

      }

    }



    /* ======================================================
       12 / MANUAL OPEN / CLOSE
       ====================================================== */

    onToggle(event) {


      event.preventDefault();


      /*
       * Theme Editor expanded preview stays deterministic.
       */

      if (
        this.previewLock
      ) {

        return;

      }


      this.manualOverride =
        true;


      this.manualStartY =
        window.scrollY;


      this.setExpanded(
        !this.expanded,
        'manual-toggle'
      );

    }



    /* ======================================================
       13 / REFRESH

       Used by Shopify Theme Editor after re-render,
       selection or reorder.
       ====================================================== */

    refresh(
      source = 'refresh'
    ) {


      if (
        this.destroyed
      ) {

        return;

      }


      this.readSettings();


      this.manualOverride =
        false;


      if (
        this.previewLock
      ) {

        this.setExpanded(
          true,
          `${source}-preview`
        );

      } else {


        this.root.classList.remove(
          'is-preview-expanded'
        );


        this.syncFromGeometry(
          source
        );

      }

    }



    /* ======================================================
       14 / STATE EVENT

       Useful later for analytics or QA without coupling
       analytics directly into this controller.
       ====================================================== */

    emitStateChange(
      source
    ) {


      this.root.dispatchEvent(
        new CustomEvent(
          'll:promo-state',
          {
            bubbles:
              true,

            detail: {
              state:
                this.expanded
                  ? 'expanded'
                  : 'collapsed',

              source,

              campaignId:
                this.root.dataset.campaignId ||
                ''
            }
          }
        )
      );

    }



    /* ======================================================
       15 / DESTROY
       ====================================================== */

    destroy() {


      if (
        this.destroyed
      ) {

        return;

      }


      this.destroyed =
        true;


      window.removeEventListener(
        'scroll',
        this.onScroll
      );


      window.removeEventListener(
        'resize',
        this.onResize
      );


      window.clearTimeout(
        this.resizeTimer
      );


      if (
        this.toggle
      ) {

        this.toggle.removeEventListener(
          'click',
          this.onToggle
        );

      }

    }


  }



  /* ========================================================
     16 / MANAGER
     ======================================================== */

  const controllers =
    new Map();



  /* ========================================================
     17 / REMOVE STALE DOM CONTROLLERS
     ======================================================== */

  const prune =
    () => {


      controllers.forEach(
        (
          controller,
          root
        ) => {


          if (
            root.isConnected
          ) {

            return;

          }


          controller.destroy();


          controllers.delete(
            root
          );

        }
      );

    };



  /* ========================================================
     18 / INITIALIZE ROOT
     ======================================================== */

  const initialize =
    root => {


      if (
        !root ||
        controllers.has(root)
      ) {

        return;

      }


      const controller =
        new LLPromoController(
          root
        );


      controllers.set(
        root,
        controller
      );

    };



  /* ========================================================
     19 / SCAN DOM
     ======================================================== */

  const scan =
    (
      context = document
    ) => {


      prune();


      /*
       * Context itself may be promo root.
       */

      if (
        context.matches &&
        context.matches(
          ROOT_SELECTOR
        )
      ) {

        initialize(
          context
        );

      }


      /*
       * Or Shopify wrapper may contain it.
       */

      if (
        context.querySelectorAll
      ) {

        context
          .querySelectorAll(
            ROOT_SELECTOR
          )
          .forEach(
            initialize
          );

      }

    };



  /* ========================================================
     20 / FIND PROMO ROOT FROM SHOPIFY EVENT
     ======================================================== */

  const findRoot =
    target => {


      if (
        !target
      ) {

        return null;

      }


      if (
        target.matches &&
        target.matches(
          ROOT_SELECTOR
        )
      ) {

        return target;

      }


      if (
        target.closest
      ) {


        const closest =
          target.closest(
            ROOT_SELECTOR
          );


        if (
          closest
        ) {

          return closest;

        }

      }


      if (
        target.querySelector
      ) {

        return target.querySelector(
          ROOT_SELECTOR
        );

      }


      return null;

    };



  /* ========================================================
     21 / DESTROY CONTROLLERS WITHIN SECTION WRAPPER
     ======================================================== */

  const destroyWithin =
    target => {


      controllers.forEach(
        (
          controller,
          root
        ) => {


          const belongs =
            (
              root === target ||
              (
                target &&
                typeof target.contains ===
                'function' &&
                target.contains(root)
              )
            );


          if (
            !belongs
          ) {

            return;

          }


          controller.destroy();


          controllers.delete(
            root
          );

        }
      );

    };



  /* ========================================================
     22 / SHOPIFY — SECTION LOAD

     Theme Editor injected fresh HTML.
     ======================================================== */

  const onSectionLoad =
    event => {


      scan(
        event.target
      );


      window.requestAnimationFrame(
        () => {


          const root =
            findRoot(
              event.target
            );


          const controller =
            root
              ? controllers.get(root)
              : null;


          if (
            controller
          ) {

            controller.refresh(
              'section-load'
            );

          }


        }
      );

    };



  /* ========================================================
     23 / SHOPIFY — SECTION UNLOAD
     ======================================================== */

  const onSectionUnload =
    event => {


      destroyWithin(
        event.target
      );

    };



  /* ========================================================
     24 / SHOPIFY — SECTION SELECT

     Do NOT force it open.

     Merchant can use:
     Vista de edición → Módulo expandido
     when they actually need the expanded state.
     ======================================================== */

  const onSectionSelect =
    event => {


      const root =
        findRoot(
          event.target
        );


      const controller =
        root
          ? controllers.get(root)
          : null;


      if (
        controller
      ) {

        controller.refresh(
          'section-select'
        );

      }

    };



  /* ========================================================
     25 / SHOPIFY — SECTION DESELECT
     ======================================================== */

  const onSectionDeselect =
    event => {


      const root =
        findRoot(
          event.target
        );


      const controller =
        root
          ? controllers.get(root)
          : null;


      if (
        controller
      ) {

        controller.refresh(
          'section-deselect'
        );

      }

    };



  /* ========================================================
     26 / SHOPIFY — SECTION REORDER
     ======================================================== */

  const onSectionReorder =
    () => {


      window.requestAnimationFrame(
        () => {


          controllers.forEach(
            controller => {


              controller.refresh(
                'section-reorder'
              );


            }
          );


        }
      );

    };



  /* ========================================================
     27 / REGISTER SHOPIFY EVENTS
     ======================================================== */

  document.addEventListener(
    'shopify:section:load',
    onSectionLoad
  );


  document.addEventListener(
    'shopify:section:unload',
    onSectionUnload
  );


  document.addEventListener(
    'shopify:section:select',
    onSectionSelect
  );


  document.addEventListener(
    'shopify:section:deselect',
    onSectionDeselect
  );


  document.addEventListener(
    'shopify:section:reorder',
    onSectionReorder
  );



  /* ========================================================
     28 / PUBLIC QA API
     ======================================================== */

  const manager = {


    version:
      VERSION,


    scan,


    refresh() {


      scan(
        document
      );


      controllers.forEach(
        controller => {


          controller.refresh(
            'manager-refresh'
          );


        }
      );

    },


    open() {


      controllers.forEach(
        controller => {


          controller.previewLock =
            false;


          controller.manualOverride =
            true;


          controller.manualStartY =
            window.scrollY;


          controller.setExpanded(
            true,
            'manager-open'
          );


        }
      );

    },


    close() {


      controllers.forEach(
        controller => {


          controller.previewLock =
            false;


          controller.manualOverride =
            true;


          controller.manualStartY =
            window.scrollY;


          controller.setExpanded(
            false,
            'manager-close'
          );


        }
      );

    },


    status() {


      const rows =
        [];


      controllers.forEach(
        controller => {


          const rect =
            controller.bar
              .getBoundingClientRect();


          rows.push({
            version:
              VERSION,

            campaign:
              controller.root.dataset.campaignId ||
              '',

            state:
              controller.expanded
                ? 'expanded'
                : 'collapsed',

            preview:
              controller.previewLock,

            openLine:
              controller.openLine,

            closeLine:
              controller.closeLine,

            barTop:
              Math.round(
                rect.top
              ),

            barHeight:
              Math.round(
                rect.height
              )
          });


        }
      );


      console.table(
        rows
      );


      return rows;

    },


    destroy() {


      controllers.forEach(
        controller => {


          controller.destroy();


        }
      );


      controllers.clear();


      document.removeEventListener(
        'shopify:section:load',
        onSectionLoad
      );


      document.removeEventListener(
        'shopify:section:unload',
        onSectionUnload
      );


      document.removeEventListener(
        'shopify:section:select',
        onSectionSelect
      );


      document.removeEventListener(
        'shopify:section:deselect',
        onSectionDeselect
      );


      document.removeEventListener(
        'shopify:section:reorder',
        onSectionReorder
      );

    }


  };



  /* ========================================================
     29 / GLOBAL API
     ======================================================== */

  window[MANAGER_KEY] =
    manager;


  window.LLCasaMotion =
    manager;



  /* ========================================================
     30 / START
     ======================================================== */

  const start =
    () => {


      scan(
        document
      );

    };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );

  } else {

    start();

  }


})();